import React, { useCallback, useEffect, useState } from 'react';

import { globalHistory } from '@reach/router';
import { Script } from 'gatsby';

import expand from '../../../static/icons/chat-icon-expand.svg';
import waitForElement from '../../helpers/waitForElement';
import { ChatContainer, ChatImage } from './LiveChat.style';

const ipScapeLiveChatId = 'MjN0dkwvTXlpU0xnTEZldVNVTDVOZz09_frame';
const FUNCTIONAL_COOKIE_CATEGORY_CODE = 'C0003';

// URL configuration
const URL_CONFIG = {
  motor: [
    '/car-insurance',
    '/modern-slavery-statement',
    '/motor-docs',
    '/claims/?product=car',
    '/contact/?product=car',
  ],
  pet: ['/pet-insurance', '/claims/?product=pet', '/contact/?product=pet'],
  blank: ['/login', '/terms-and-conditions'],
  partialBlank: ['/claims', '/contact'],
  validProductQueries: ['?product=home', '?product=car', '?product=pet'],
} as const;

type LiveChatType = 'home' | 'motor' | 'pet' | 'none';

interface ChatState {
  isZendeskEnabled: boolean;
  isImpactWidgetVisible: boolean;
  isHome: boolean;
  isPet: boolean;
}

const ZENDESK_SCRIPT = `
  window.zEmbed ||
    (function() {
      var queue = [];
      window.zEmbed = function() {
        queue.push(arguments);
      };
      window.zE = window.zE || window.zEmbed;
      document.zendeskHost = 'policyexperthelp.zendesk.com';
      document.zEQueue = queue;
    })();
`;

const LiveChat = () => {
  const [chatState, setChatState] = useState<ChatState>({
    isZendeskEnabled: false,
    isImpactWidgetVisible: true,
    isHome: false,
    isPet: false,
  });

  // Helper function to update multiple state properties at once
  const updateChatState = useCallback((updates: Partial<ChatState>) => {
    setChatState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Helper function to check if URL matches any patterns
  const isUrlMatching = useCallback(
    (url: string, patterns: readonly string[]): boolean => {
      return patterns.some((pattern) => url.includes(pattern));
    },
    []
  );

  // Helper function to check if query parameter is valid
  const isValidProductQuery = useCallback((queryParam: string): boolean => {
    return URL_CONFIG.validProductQueries.some(
      (validQuery) => queryParam === validQuery
    );
  }, []);

  // Helper function to remove chat frame
  const removeChatFrame = useCallback(() => {
    window.Chat?.frame?.remove();
  }, []);

  // Helper function to initialize motor chat
  const initializeMotorChat = useCallback(() => {
    removeChatFrame();
    window.Chat?.init({
      container: 'chat_ipscape_container',
      key: 'MjN0dkwvTXlpU0xnTEZldVNVTDVOZz09',
      src: 'surething.ourcontactcentre.com/webchat',
      style: { right: '0%' },
    });
  }, [removeChatFrame]);

  const handleShowImpactLiveChat = useCallback(() => {
    updateChatState({ isImpactWidgetVisible: false });
    if (window.zE) {
      window.zE('webWidget', 'show');
      window.zE('webWidget', 'open');
    }
  }, [updateChatState]);

  const handleLiveChatSwitch = useCallback(
    (desiredLiveChat: LiveChatType) => {
      // Always close Zendesk widget first when switching chat types
      if (window.zE) {
        window.zE('webWidget', 'hide');
      }

      const actions = {
        home: () => removeChatFrame(),
        pet: () => removeChatFrame(),
        motor: () => initializeMotorChat(),
        none: () => removeChatFrame(),
      };

      actions[desiredLiveChat]?.();
    },
    [removeChatFrame, initializeMotorChat]
  );

  const configureZendeskWidget = useCallback(() => {
    if (window.zE) {
      window.zE('webWidget', 'hide');
      window.zE('webWidget:on', 'close', () => {
        window.zE('webWidget', 'hide');
        updateChatState({ isImpactWidgetVisible: true });
      });
    }
  }, [updateChatState]);

  const configureZendeskSettings = useCallback((isPet: boolean) => {
    if (typeof window !== 'undefined' && window.zE) {
      const enabledDepartments = isPet
        ? ['pet customer service']
        : ['customer service', 'renewals'];

      const topSuggested = isPet
        ? { labels: ['petlivechatrecommendedarticle'] }
        : { search: 'home insurance' };

      window.zE('webWidget', 'updateSettings', {
        webWidget: {
          chat: {
            departments: { enabled: enabledDepartments },
          },
        },
      });

      window.zE('webWidget', 'helpCenter:setSuggestions', topSuggested);
    }
  }, []);

  const determineChatType = useCallback(
    (url: string, queryParam: string): LiveChatType => {
      // Check blank pages
      if (isUrlMatching(url, URL_CONFIG.blank)) {
        return 'none';
      }

      // Check partial blank pages
      if (
        isUrlMatching(url, URL_CONFIG.partialBlank) &&
        !isValidProductQuery(queryParam)
      ) {
        return 'none';
      }

      // Check motor URLs
      if (isUrlMatching(url, URL_CONFIG.motor)) {
        return 'motor';
      }

      // Check pet URLs
      if (isUrlMatching(url, URL_CONFIG.pet)) {
        return 'pet';
      }

      // Default to home
      return 'home';
    },
    [isUrlMatching, isValidProductQuery]
  );

  const checkUrl = useCallback(() => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const queryParam =
      typeof window !== 'undefined' ? window.location.search : '';

    const chatType = determineChatType(url, queryParam);
    const isHomePage = window.location.pathname === '/';

    // Determine state updates based on chat type
    const stateUpdates: Partial<ChatState> = {
      isHome: chatType === 'home',
      isPet: chatType === 'pet',
      isImpactWidgetVisible: chatType === 'home' || chatType === 'pet',
    };

    // Special handling for home page
    if (isHomePage && chatType === 'home') {
      stateUpdates.isImpactWidgetVisible = false;
      handleLiveChatSwitch('none');
    } else {
      handleLiveChatSwitch(chatType);
    }

    // Special handling for motor chat
    if (chatType === 'motor') {
      stateUpdates.isImpactWidgetVisible = false;
      waitForElement(ipScapeLiveChatId).then(() => {
        document
          .getElementById(ipScapeLiveChatId)
          ?.classList.add('ipscape-chat-widget');
      });
    }

    updateChatState(stateUpdates);
  }, [determineChatType, handleLiveChatSwitch, updateChatState]);

  // Effect for OneTrust and routing
  useEffect(() => {
    const handleOneTrustUpdate = (event: any) => {
      const isEnabled = event.detail.includes(FUNCTIONAL_COOKIE_CATEGORY_CODE);
      updateChatState({ isZendeskEnabled: isEnabled });

      if (isEnabled) {
        configureZendeskWidget();
      }
    };

    const oneTrustGroups = window?.OnetrustActiveGroups || '';
    if (oneTrustGroups.includes(FUNCTIONAL_COOKIE_CATEGORY_CODE)) {
      updateChatState({ isZendeskEnabled: true });
      configureZendeskWidget();
    }

    window.addEventListener('OneTrustGroupsUpdated', handleOneTrustUpdate);
    checkUrl();

    return globalHistory.listen(({ action }) => {
      if (action === 'PUSH' || action === 'POP') {
        checkUrl();
      }
    });
  }, [checkUrl, configureZendeskWidget, updateChatState]);

  // Effect for Zendesk configuration
  useEffect(() => {
    if (chatState.isZendeskEnabled) {
      configureZendeskWidget();
    }
  }, [chatState.isZendeskEnabled, configureZendeskWidget]);

  // Effect for Zendesk settings
  useEffect(() => {
    if ((chatState.isHome || chatState.isPet) && chatState.isZendeskEnabled) {
      configureZendeskSettings(chatState.isPet);
    }
  }, [
    chatState.isHome,
    chatState.isPet,
    chatState.isZendeskEnabled,
    configureZendeskSettings,
  ]);

  return (
    <>
      <div id="chat_ipscape_container" />
      <Script src="https://surething.ourcontactcentre.com/webchat/chat-uri.js" />

      {chatState.isZendeskEnabled && (
        <>
          <Script>{ZENDESK_SCRIPT}</Script>
          <Script
            src="https://assets.zendesk.com/embeddable_framework/main.js"
            data-ze-csp="true"
            async
            defer
          />
        </>
      )}

      {chatState.isImpactWidgetVisible &&
        (chatState.isHome || chatState.isPet) &&
        chatState.isZendeskEnabled && (
          <ChatContainer
            onClick={handleShowImpactLiveChat}
            id="zendesk-chat-widget"
          >
            Chat now
            <ChatImage src={expand} alt="expand live chat" />
          </ChatContainer>
        )}
    </>
  );
};

export default LiveChat;
